import { OnInit, Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ListService } from '@abp/ng.core';
import {
  CardComponent,
  CardBodyComponent,
  ConfirmationService,
} from '@abp/ng.theme.shared';
import {
  BaseCrudComponent,
  ActionTypes,
  createImportExcelModalConfig,
  createLocalizationKey,
  CrudPage,
  ExcelImportErrorsExportLike,
  ExcelImportResultLike,
  GenericForm,
  IMPORT_MAX_SIZE_MB,
  IActionsSchema,
  IActionModal,
  ICrudService,
  ModalBuilder,
  resolveActions,
  ZendraLocalizationServices,
  ZnExcelImportErrorsModal,
  ZnFileUploader,
  DEFAULT_TOASTER_LIFE,
} from '@zendra/shared';
import { Observable, map, of, switchMap, tap } from 'rxjs';
import {
  LidarInventoryDataEntryCreateDto,
  LidarInventoryDataEntryUpdateDto,
} from '../proxy/inventory/dtos/lidar-inventory-data-entry';
import { LidarInventoryDataEntryService } from '../proxy/inventory/controllers';
import {
  ACTION_IDS,
  LIDARINVENTORYDATAENTRY_ACTIONS,
  LIDARINVENTORYDATAENTRY_CONFIG,
  LIDARINVENTORYDATAENTRY_FIELDS,
} from './lidar-inventory-data-entry.config';
import { waitForLidarProjectionChanges } from './lidar-projection-consistency';
import { LidarProjectionChangesDto } from 'projects/orchestrator/src/lib/proxy/orchestrator/dtos/lidar-inventory/models';
import { LidarInventoryDataEntryPlanningOrchestratorService } from 'orchestrator';
import {
  GetLidarInventoryDataEntryProjectionListInputDto,
  LidarInventoryDataEntryProjectionDto,
  LidarInventoryDataEntryProjectionExcelDownloadDto,
} from 'projects/orchestrator/src/lib/proxy/upm/zendra/cms/projection/lidar-inventory-data-entries/models';

const IMPORT_MODAL_TITLE = createLocalizationKey(
  'Action.Modal.ImportMeasurementLidarInventoryDataEntry.Title',
  ZendraLocalizationServices.Inventory,
);
const IMPORT_AGAIN_LABEL = 'LanguageService::Action:ExcelImport.ImportAgain';
const IMPORT_TEMPLATE_FILE_NAME = 'LidarInventoryDataEntryImportTemplate.xlsx';

type LidarInventoryDataEntryImportResult = ExcelImportResultLike<unknown> & {
  fileName?: string;
};

@Component({
  selector: 'app-lidar-inventory-data-entry',
  imports: [
    CardComponent,
    CardBodyComponent,
    CrudPage,
    GenericForm,
  ],
  providers: [ListService, LidarInventoryDataEntryService, ConfirmationService],
  templateUrl: './lidar-inventory-data-entry.html',
  styleUrl: './lidar-inventory-data-entry.scss',
})
export class LidarInventoryDataEntry extends BaseCrudComponent<
  LidarInventoryDataEntryProjectionDto,
  LidarInventoryDataEntryCreateDto,
  LidarInventoryDataEntryUpdateDto,
  LidarInventoryDataEntryProjectionExcelDownloadDto
> implements OnInit {
  private readonly inventoryService = inject(LidarInventoryDataEntryService);
  private readonly orchestratorService = inject(LidarInventoryDataEntryPlanningOrchestratorService);
  private readonly modalService = inject(ModalBuilder);
  private readonly destroyRef = inject(DestroyRef);
  service: ICrudService<
    LidarInventoryDataEntryProjectionDto,
    LidarInventoryDataEntryCreateDto,
    LidarInventoryDataEntryUpdateDto,
    LidarInventoryDataEntryProjectionExcelDownloadDto
  > = {
    create: input => this.inventoryService.create(input),
    update: (id, input) => this.inventoryService.update(id, input),
    deleteByIds: ids => this.inventoryService.deleteByIds(ids),
    getList: (query: GetLidarInventoryDataEntryProjectionListInputDto) =>
      this.orchestratorService.getList(query),
    getListByIds: input => this.inventoryService.getListByIds(input),
    getDownloadToken: () => this.orchestratorService.getDownloadToken(),
    getListAsExcelFile: input => this.orchestratorService.getListAsExcelFile(input),
    projectionConsistency: {
      ...this.createProjectionConsistency(this.orchestratorService, 'lidarInventoryDataEntryId'),
      onTimeout: () => this.notifyProjectionDelay(),
    },
  };
  config = LIDARINVENTORYDATAENTRY_CONFIG;
  fields = LIDARINVENTORYDATAENTRY_FIELDS;
  actions: IActionsSchema | null = null;

  private readonly triggerMap: Record<number, () => void> = {
    [ACTION_IDS.importExcelModal]: () => this.openImportModal(),
    [ACTION_IDS.downloadExcelTemplateTrigger]: () => this.downloadExcelTemplate(),
  };

  ngOnInit(): void {
    const actions = resolveActions(LIDARINVENTORYDATAENTRY_ACTIONS, this.triggerMap);
    this.actions = this.withGridRefreshOnActionCompleted({
      ...actions,
      overTable: actions.overTable?.map(action => {
        const waitsForProjection = action.id === ACTION_IDS.undoLidarInventoryRoutine
          || action.id === ACTION_IDS.runLidarInventoryRoutine
          || action.id === ACTION_IDS.deleteMeasurement;

        if (!waitsForProjection || action.actionType !== ActionTypes.Modal) {
          return action;
        }

        let inputs: IActionModal['config']['inputs'];
        if (action.id === ACTION_IDS.undoLidarInventoryRoutine) {
          inputs = {
            waitForProjection: (changes: LidarProjectionChangesDto) => this.waitForChanges(changes),
          };
        } else if (action.id === ACTION_IDS.runLidarInventoryRoutine) {
          inputs = {
            runRoutineService: {
              executeLidarInventoryRoutine: (input: { meditionNumbers: number[] }) =>
                this.orchestratorService.executeLidarInventoryRoutine(input).pipe(
                  switchMap(changes => this.waitForChanges(changes)),
                ),
            },
          };
        } else {
          inputs = {
            deleteService: {
              deleteByMeditionNumber: (input: { meditionNumber: number }) =>
                this.orchestratorService.deleteByMeditionNumber(input).pipe(
                  switchMap(changes => this.waitForChanges(changes)),
                ),
            },
          };
        }

        return {
          ...action,
          config: {
            ...(action as IActionModal).config,
            inputs: {
              ...(action as IActionModal).config.inputs,
              ...inputs,
            },
          },
        };
      }),
    });
  }

  private notifyProjectionDelay(): void {
    this.toaster.warn('Inventory::ProjectionUpdateDelayed', null, { life: DEFAULT_TOASTER_LIFE });
  }

  private waitForChanges(changes: LidarProjectionChangesDto): Observable<boolean> {
    return waitForLidarProjectionChanges(changes, input => this.orchestratorService.getList(input)).pipe(
      tap(consistent => { if (!consistent) this.notifyProjectionDelay(); }),
      takeUntilDestroyed(this.destroyRef),
    );
  }

  protected prepareUpdateDto(data: any, selected: LidarInventoryDataEntryProjectionDto): LidarInventoryDataEntryUpdateDto {
    return {
      inventoryDate: data.inventoryDate,
      standNumber: data.standNumber,
      plotCount: data.plotCount,
      treeCount: data.treeCount,
      averageDiameter: data.averageDiameter,
      radius: data.radius,
      area: data.area,
      averageVolume: data.averageVolume,
      treeVolume: data.treeVolume,
      basalArea: data.basalArea,
      dominantHeight: data.dominantHeight,
      maximumHeight: data.maximumHeight,
      averageHeight: data.averageHeight,
      individualVolume: data.individualVolume,
      treeAge: data.treeAge,
      error: data.error,
      minimumVolume: data.minimumVolume,
      maximumVolume: data.maximumVolume,
      meditionNumber: data.meditionNumber,
      isProcessed: data.isProcessed,
      finalDecision_T2_T4: data.finalDecision_T2_T4,
      dab: data.dab,
      t2PlusT4_10Pct: data.t2PlusT4_10Pct,
      t2PlusT4_20Pct: data.t2PlusT4_20Pct,
      volume_10Pct: data.volume_10Pct,
      volume_20Pct: data.volume_20Pct,
      linew_Min: data.linew_Min,
      linew_Mean: data.linew_Mean,
      linew_Max: data.linew_Max,
      teamId: data.teamId,
      inventorySubTypeId: data.inventorySubTypeId,
      concurrencyStamp: selected.concurrencyStamp,
    };
  }

  protected getEntityName(): string {
    return 'LidarInventoryDataEntry';
  }

  private openImportModal(): void {
    const modalRef = this.modalService.openModal(
      ZnFileUploader,
      createImportExcelModalConfig({
        component: ZnFileUploader,
        title: IMPORT_MODAL_TITLE,
        subTitle: 'Inventory::Entity.LidarInventoryDataEntry',
        inputs: {
          maxSizeMb: IMPORT_MAX_SIZE_MB,
          importFile: (formData: FormData) =>
            this.orchestratorService.importLidarInventoryDataEntryMeasurement(formData).pipe(
              switchMap(result => result.hasErrors || !result.projectionChanges
                ? of(result)
                : this.waitForChanges(result.projectionChanges).pipe(map(() => result))),
              takeUntilDestroyed(this.destroyRef),
            ),
        },
      }),
    );

    modalRef.closed
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result?: LidarInventoryDataEntryImportResult) => {
        if (result?.hasErrors) {
          this.openImportErrorsModal(result);
          return;
        }

        if (result) {
          this.refreshGrid();
        }
      });
  }

  private openImportErrorsModal(result: LidarInventoryDataEntryImportResult): void {
    const modalRef = this.modalService.openModal(
      ZnExcelImportErrorsModal,
      createImportExcelModalConfig({
        component: ZnExcelImportErrorsModal,
        title: IMPORT_MODAL_TITLE,
        subTitle: 'Inventory::Entity.LidarInventoryDataEntry',
        confirmLabel: IMPORT_AGAIN_LABEL,
        dismissLabel: 'LanguageService::Close',
        size: 'lg',
        inputs: {
          fileName: result.fileName ?? '',
          errors: result.errors,
          title: IMPORT_MODAL_TITLE,
          subTitle: 'Inventory::Entity.LidarInventoryDataEntry',
          exportErrors: (input: ExcelImportErrorsExportLike) =>
            this.orchestratorService.exportLidarInventoryDataEntryImportErrors(input),
        },
      }),
    );

    modalRef.closed
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((modalResult?: { retry?: boolean }) => {
        if (modalResult?.retry) {
          this.openImportModal();
        }
      });
  }

  private downloadExcelTemplate(): void {
    this.orchestratorService.getLidarInventoryDataEntryImportTemplate().subscribe({
      next: result => {
        this.toaster.success('LanguageService::Actions:DownloadTemplateMessage', "", { life: DEFAULT_TOASTER_LIFE });
        this.downloadFile(result, IMPORT_TEMPLATE_FILE_NAME);
      },
      error: error => {
        this.toaster.error(error, "", { life: DEFAULT_TOASTER_LIFE });
      },
    });
  }
}
